import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { Badge, Button, Card, Spinner, Toast } from '../../components/ui';
import { useT } from '../../i18n';
import { api } from '../../lib/api';
import { parseSettingValue, personLabel } from '../../lib/format';
import { haptic } from '../../lib/tma';

export function AdminWaitlistPage() {
  const t = useT();
  const qc = useQueryClient();
  const [credits, setCredits] = useState('300');
  const [toast, setToast] = useState<string | null>(null);

  const flash = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 3500);
  };

  const list = useQuery({
    queryKey: ['admin', 'waitlist'],
    queryFn: () => api.adminListWaitlist(),
  });

  const admit = useMutation({
    mutationFn: (input: { id: number; credits: number }) => api.adminAdmit(input.id, input.credits),
    onSuccess: (res) => {
      haptic('success');
      flash(res.notified ? t.admin.waitlistNotified : t.admin.waitlistNotNotified);
    },
    onError: () => flash(t.common.failed),
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'waitlist'] }),
  });

  const parsedCredits = parseSettingValue(credits);
  const canAdmit = parsedCredits !== null && parsedCredits <= 100_000;

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-12 safe-top safe-bottom">
      <PageHeader title={t.admin.waitlist} to="/admin" />

      <Card variant="surface" padding="md" className="mb-4">
        <label className="block text-xs text-hint mb-1" htmlFor="admit-credits">
          {t.admin.waitlistCredits}
        </label>
        <input
          id="admit-credits"
          inputMode="numeric"
          value={credits}
          onChange={(e) => setCredits(e.target.value)}
          className="w-full rounded-lg px-4 py-2.5 text-sm bg-surface-2 border-0 focus:outline-none focus:ring-2 focus:ring-ink/70"
        />
        {!canAdmit && <p className="text-xs text-destructive mt-1">{t.admin.settingsBad}</p>}
      </Card>

      {list.isLoading && (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      )}
      {list.isError && <p className="text-sm text-hint">{t.common.failed}</p>}
      {list.data && list.data.entries.length === 0 && (
        <p className="text-sm text-hint">{t.admin.waitlistEmpty}</p>
      )}

      <div className="space-y-2">
        {list.data?.entries.map((e) => (
          <Card key={e.id} variant="surface" padding="md">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-semibold">{personLabel(e)}</span>
              {e.invitedAt ? (
                <Badge tone="neutral">{t.admin.waitlistAdmitted}</Badge>
              ) : (
                <Button
                  variant="ink"
                  size="sm"
                  disabled={!canAdmit || admit.isPending}
                  onClick={() => {
                    if (parsedCredits === null) return;
                    admit.mutate({ id: e.id, credits: parsedCredits });
                  }}
                >
                  {t.admin.waitlistAdmit}
                </Button>
              )}
            </div>
            {e.note && <p className="text-xs text-hint mt-1 break-words">{e.note}</p>}
          </Card>
        ))}
      </div>

      {toast && (
        <Toast tone="neutral" placement="fixed">
          {toast}
        </Toast>
      )}
    </div>
  );
}
